import { useEffect, useState } from 'react';
import Card from '../../../components/Card.jsx';
import Button from '../../../components/Button.jsx';
import Chip from '../../../components/Chip.jsx';
import StatCard from '../../../components/StatCard.jsx';
import DataTable from '../../../components/DataTable.jsx';
import { mlApi } from '../../../lib/apiClient.js';
import ConfigPanel from '../ConfigPanel.jsx';
import StrategyPreview from '../StrategyPreview.jsx';
import ErrorNote from '../ErrorNote.jsx';
import { isBacktestPeriodValid } from '../../../utils/backtestPeriod.js';

const FEATURE_OPTIONS = ['ema_20', 'sma_20', 'rsi_14', 'macd', 'atr_14', 'bb_pctb', 'adx_14'];

const IMPORTANCE_COLUMNS = [
  { key: 'feature', header: 'Feature' },
  {
    key: 'importance',
    header: 'Importance',
    numeric: true,
    render: (r) => `${(r.importance * 100).toFixed(1)}%`,
  },
];

/**
 * ML research: task/features/label + xgboost params, CPU or GPU device
 * (device:'cuda' needs a GPU-enabled seat — same gate as ConfigPanel's
 * compute toggle). `/api/research/train` = one model, `/api/research/sweep`
 * = hyperparam leaderboard (OBT_INTEGRATION_SPEC.md §1 Research + §2).
 */
export default function MlResearchMode({ config, onConfigChange, wallet, onRan, lastRun, onSaveRun }) {
  const [task, setTask] = useState('classification');
  const [features, setFeatures] = useState(['ema_20', 'rsi_14', 'atr_14']);
  const [horizon, setHorizon] = useState(5);
  const [binary, setBinary] = useState(true);
  const [params, setParams] = useState({ n_estimators: 200, max_depth: 5, learning_rate: 0.05 });
  const [running, setRunning] = useState(null); // 'train' | 'sweep' | null
  const [trainResult, setTrainResult] = useState(() => lastRun?.result?.train || null);
  const [sweepResult, setSweepResult] = useState(() => lastRun?.result?.sweep || null);
  const [error, setError] = useState(null);
  useEffect(() => {
    const view = lastRun?.result;
    if (view?.status !== 'complete') return;
    if (view.meta?.runType === 'research_train') setTrainResult(view.result);
    if (view.meta?.runType === 'research_sweep') setSweepResult(view.result);
  }, [lastRun?.result]);

  const gpuEnabled = Boolean(wallet?.gpuEnabled);
  const device = config.compute === 'gpu' ? 'cuda' : 'cpu';

  function toggleFeature(f) {
    setFeatures((prev) => (prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]));
  }

  const request = {
    task,
    features: features.map((name) => ({ name, ref: { feature: name } })),
    label: { type: 'forward_return', horizon, binary: task !== 'regression' && binary },
    params,
    device,
    symbols: config.symbols,
    period: config.period,
    n_splits: 5,
  };

  async function train() {
    setError(null);
    setRunning('train');
    try {
      const data = await mlApi.train(request);
      setTrainResult(data.status === 'complete' ? data.result : null);
      onSaveRun?.(data);
      onRan?.();
    } catch (err) {
      setError(err);
    } finally {
      setRunning(null);
    }
  }

  async function sweep() {
    setError(null);
    setRunning('sweep');
    try {
      const data = await mlApi.sweep({
        ...request,
        grid: { max_depth: [3, 5, 7], learning_rate: [0.01, 0.05, 0.1] },
      });
      setSweepResult(data.status === 'complete' ? data.result : null);
      onSaveRun?.(data);
      onRan?.();
    } catch (err) {
      setError(err);
    } finally {
      setRunning(null);
    }
  }

  return (
    <div className="aq-grid aq-grid--2">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <ConfigPanel config={config} onChange={onConfigChange} wallet={wallet} allowGpu />

        <Card>
          <h3 className="heading" style={{ fontSize: 16, marginBottom: 12 }}>
            Model research
          </h3>

          <div className="aq-bt-params">
            <label className="aq-field">
              <span>Task</span>
              <select value={task} onChange={(e) => setTask(e.target.value)}>
                <option value="classification">Classification (direction)</option>
                <option value="regression">Regression (return)</option>
              </select>
            </label>
            <label className="aq-field">
              <span>Label horizon (bars)</span>
              <input type="number" value={horizon} onChange={(e) => setHorizon(Number(e.target.value))} />
            </label>
            {task !== 'regression' && (
              <label className="aq-field">
                <span>Binary label</span>
                <select value={binary ? 'yes' : 'no'} onChange={(e) => setBinary(e.target.value === 'yes')}>
                  <option value="yes">Up/Down (binary)</option>
                  <option value="no">Multi-class</option>
                </select>
              </label>
            )}
            <label className="aq-field">
              <span>n_estimators</span>
              <input
                type="number"
                value={params.n_estimators}
                onChange={(e) => setParams((p) => ({ ...p, n_estimators: Number(e.target.value) }))}
              />
            </label>
            <label className="aq-field">
              <span>max_depth</span>
              <input
                type="number"
                value={params.max_depth}
                onChange={(e) => setParams((p) => ({ ...p, max_depth: Number(e.target.value) }))}
              />
            </label>
            <label className="aq-field">
              <span>learning_rate</span>
              <input
                type="number"
                step="0.01"
                value={params.learning_rate}
                onChange={(e) => setParams((p) => ({ ...p, learning_rate: Number(e.target.value) }))}
              />
            </label>
          </div>

          <div className="aq-field" style={{ marginBottom: 4 }}>
            <span>Features</span>
            <div className="aq-symbol-picker">
              {FEATURE_OPTIONS.map((f) => (
                <label key={f} className="aq-symbol-chip">
                  <input type="checkbox" checked={features.includes(f)} onChange={() => toggleFeature(f)} />
                  <span>{f}</span>
                </label>
              ))}
            </div>
          </div>

          <StrategyPreview title="research request preview" value={request} />

          <div style={{ marginTop: 14, display: 'flex', gap: 10 }}>
            <Button disabled={running !== null || features.length === 0 || !isBacktestPeriodValid(config.period)} onClick={train}>
              {running === 'train' ? 'Training…' : `Train model (${device === 'cuda' ? 'GPU' : 'CPU'})`}
            </Button>
            <Button variant="secondary" disabled={running !== null || features.length === 0 || !isBacktestPeriodValid(config.period)} onClick={sweep}>
              {running === 'sweep' ? 'Sweeping…' : 'Sweep hyperparameters'}
            </Button>
          </div>
          {device === 'cuda' && !gpuEnabled && (
            <p className="aq-note">
              GPU selected but this seat is CPU-only — the run will be rejected with an upgrade prompt (402
              gpu_tier_required), matching the engine's tier gate.
            </p>
          )}
        </Card>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Card>
          <h3 className="heading" style={{ fontSize: 16, marginBottom: 12 }}>
            Train result
          </h3>
          <ErrorNote error={error} />
          {!trainResult && !error && <p className="aq-module-desc">Train a model to see metrics + feature importance.</p>}
          {trainResult && (
            <>
              <div className="aq-grid aq-grid--3">
                {Object.entries(trainResult.metrics || {}).map(([k, v]) => (
                  <StatCard key={k} label={k.toUpperCase()} value={v} />
                ))}
              </div>
              <h4 className="heading" style={{ fontSize: 13, margin: '16px 0 8px' }}>
                Feature importance
              </h4>
              <DataTable columns={IMPORTANCE_COLUMNS} rows={trainResult.featureImportance} />
              <div className="aq-metric-row">
                <span>
                  Charged <strong className="num">${trainResult.credit?.charged?.toFixed?.(4)}</strong>
                </span>
                <Chip tone="info">
                  {trainResult.model} · {trainResult.device === 'cuda' ? 'GPU' : 'CPU'}
                </Chip>
              </div>
            </>
          )}
        </Card>

        <Card>
          <h3 className="heading" style={{ fontSize: 16, marginBottom: 12 }}>
            Sweep leaderboard
          </h3>
          {!sweepResult && <p className="aq-module-desc">Sweep hyperparameters to see a leaderboard.</p>}
          {sweepResult && (
            <DataTable
              columns={[
                { key: 'params', header: 'Params', render: (r) => JSON.stringify(r.params) },
                { key: 'metric', header: (sweepResult.metricKey || 'mean_score').toUpperCase(), numeric: true, render: (r) => r[sweepResult.metricKey || 'mean_score'] },
              ]}
              rows={sweepResult.leaderboard.map((r, i) => ({ ...r, id: i }))}
            />
          )}
        </Card>
      </div>
    </div>
  );
}
